/**
 * ExportMenu - Dropdown export button with Snapshot, Email, and PPT options.
 *
 * Replaces the old single-action GenerateDeckButton with a unified export menu.
 */
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { authApi } from '../services/authApi';
import { useCreateSnapshot } from '../hooks/useSnapshot';

interface GenerateDeckButtonProps {
  title: string;
  drugNames: string[];
  messages: { role: string; content: string }[];
  disabled?: boolean;
}

const GenerateDeckButton: React.FC<GenerateDeckButtonProps> = ({
  title,
  drugNames,
  messages,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isGeneratingDocx, setIsGeneratingDocx] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // Tracks whether the mouse press that produced the current click STARTED
  // on the backdrop. A drag-select of the share URL that ends outside the
  // panel fires `click` on the nearest common ancestor (the backdrop), which
  // would otherwise dismiss the dialog mid-selection.
  const backdropPressRef = useRef(false);
  const { createSnapshot, isCreating } = useCreateSnapshot();

  const hasMessages = messages.filter(m => m.role === 'assistant').length > 0;

  // Close menu on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const showStatus = (msg: string) => {
    setStatus(msg);
    setTimeout(() => setStatus(null), 3000);
  };

  // --- Snapshot ---
  const handleSnapshot = async () => {
    setIsOpen(false);
    try {
      const result = await createSnapshot({
        query: title,
        drug_results: [],
        metadata: {
          type: 'qa_conversation',
          drug_names: drugNames,
          messages: messages,
        },
        summary: messages.filter(m => m.role === 'assistant').map(m => m.content).join('\n\n'),
        result_count: messages.length,
      });
      setShareUrl(`${window.location.origin}/snapshot/${result.share_token}`);
      setShowShareDialog(true);
      setCopied(false);
    } catch {
      showStatus('Failed');
    }
  };

  // --- Email ---
  const handleEmail = () => {
    setIsOpen(false);
    const subject = encodeURIComponent(title);
    // Build plain-text body from messages (strip HTML)
    const body = messages
      .map(m => {
        const role = m.role === 'user' ? 'Q' : 'A';
        const text = m.content
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<\/p>/gi, '\n\n')
          .replace(/<\/li>/gi, '\n')
          .replace(/<li>/gi, '- ')
          .replace(/<[^>]+>/g, '')
          .replace(/&nbsp;/g, ' ')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/\n{3,}/g, '\n\n')
          .trim();
        return `${role}: ${text}`;
      })
      .join('\n\n');
    const encodedBody = encodeURIComponent(body.slice(0, 1800)); // mailto has length limits
    window.location.href = `mailto:?subject=${subject}&body=${encodedBody}`;
  };

  // --- DOCX ---
  const handleDocx = async () => {
    setIsOpen(false);
    if (messages.length === 0) return;
    setIsGeneratingDocx(true);

    try {
      const blob = await authApi.generateReport({
        title,
        drug_names: drugNames,
        conversation_messages: messages,
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 30)}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to generate report:', err);
      showStatus('Failed');
    } finally {
      setIsGeneratingDocx(false);
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const isBusy = isGeneratingDocx || isCreating;

  return (
    <>
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setIsOpen(v => !v)}
          disabled={disabled || !hasMessages || isBusy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg border border-slate-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          title={hasMessages ? 'Export conversation' : 'Chat first to export'}
        >
          {isBusy ? (
            <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          )}
          {isBusy ? 'Exporting...' : status || 'Export'}
          <svg className="w-3 h-3 ml-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown menu */}
        {isOpen && (
          <div className="absolute bottom-full left-0 mb-1 w-52 bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-50">
            <button
              onClick={handleSnapshot}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
            >
              <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              Share link
            </button>
            <button
              onClick={handleEmail}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
            >
              <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Email
            </button>
            <button
              onClick={handleDocx}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
            >
              <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Word Document (.docx)
            </button>
          </div>
        )}
      </div>

      {/* Share URL Dialog.
          Portaled to <body>: the CEPI panel is rendered `embedded` inside a
          `position: sticky` sidebar column (DrugDetailPage.tsx), and sticky
          always creates a stacking context. Rendered inline, this dialog's
          z-[60] is scoped to that column, so it loses the paint order to the
          document viewer's invisible OCR text layer (zIndex 10 in the ROOT
          stacking context, PageRenderer.tsx / FdaLetterPageViewer.tsx). The
          dialog still paints, but the text layer swallows every click and the
          page appears frozen. The portal puts it in the root stacking context
          where z-[60] means what it says. */}
      {showShareDialog && createPortal(
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30"
          onMouseDown={(e) => { backdropPressRef.current = e.target === e.currentTarget; }}
          onClick={(e) => {
            const fromBackdrop = backdropPressRef.current;
            backdropPressRef.current = false;
            if (fromBackdrop && e.target === e.currentTarget) setShowShareDialog(false);
          }}
        >
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Conversation saved</h3>
            <p className="text-sm text-slate-500 mb-4">Anyone with this link can view this Q&A (read-only).</p>
            <div className="flex gap-2">
              <input
                readOnly
                value={shareUrl}
                className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-700"
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={handleCopyUrl}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  copied
                    ? 'bg-green-100 text-green-700 border border-green-200'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <button
              onClick={() => setShowShareDialog(false)}
              className="mt-4 w-full text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default GenerateDeckButton;
